import { css, cx } from '@fable/theme'
import { FlexBox, Icon, Text } from '@fable/components'
import { useReadingGoals } from '../../lib/ctx/reading_goals'
import { CreateReadingGoalButton } from './CreateReadingGoalButton'
import pluralize from 'pluralize'
import { formatK } from '@fable/utils'
import {
  ReadingGoalSuggestion as SuggestionType,
  ReadingGoalType,
} from '../../lib/types'

import illo from './assets/flower-cards.webp'
import illoPortrait from './assets/flower-cards-portrait.webp'
import { useStatsTheme } from '../../theme/ctx'
import { useStatsPermission } from '../../lib/hooks'
import { useStats } from '../../lib/ctx/stats'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useRef } from 'react'

import badge from '../../lib/assets/icons/fable-premium-lock-badge.webp'
import { showPlusUpsell } from '../../lib/utils'

interface Props {
  suggestion?: SuggestionType
  slim?: boolean
  onClose?: () => void
  closable?: boolean
  unlocked?: boolean
  forceUpsell?: boolean
}

export const ReadingGoalSuggestion = ({
  suggestion,
  slim = false,
  onClose,
  closable = false,
  unlocked = false,
  forceUpsell = false,
}: Props) => {
  const [period, createReadingGoal, closedSuggestions] = useReadingGoals(
    (s) => [s.period, s.createReadingGoal, s.closedSuggestions]
  )
  const theme = useStatsTheme((s) => s.theme)
  const isSelfView = useStats((s) => s.isSelfView)
  const { isAndroid, isPlusUser } = useStatsPermission()
  const isClosed = closedSuggestions.includes(period)
  const container = useRef<HTMLDivElement>(null)
  const hideLock = isPlusUser || unlocked

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: 'bottom bottom',
          once: true,
        },
      })

      tl.to('.anim__suggestion-card', {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.inOut',
      })
    },
    { scope: container }
  )

  const getLabel = () => {
    const labels: Record<ReadingGoalType, string> = {
      books_finished: 'book',
      pages_read: 'page',
      active_days: 'day',
    }

    if (!suggestion) return ''

    const suffix = pluralize(
      labels[suggestion.goal_type],
      suggestion.number_goal
    )
    const value = formatK(suggestion.number_goal).toString()

    return `${value} ${suffix}`
  }

  const handleCreateGoal = () => {
    if (!isSelfView || isAndroid) return
    if (forceUpsell || (!unlocked && !isPlusUser)) {
      showPlusUpsell()
      return
    }

    createReadingGoal(suggestion)
  }

  const handleClose = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    if (closable && onClose) onClose()
  }

  if (isClosed) return null

  return (
    <div
      ref={container}
      className={css`
        width: 100%;
        height: 100%;
      `}
    >
      <FlexBox
        role="button"
        aria-label="create goal"
        data-testid="reading-goal-suggestion"
        className={cx(
          'anim__suggestion-card',
          css`
            padding: 12px 8px;
            position: relative;
            width: 100%;
            height: 100%;
            border-radius: 16px;
            gap: 20px;
            background-color: ${theme.colors.section.bgSecondary};
            align-items: center;
            justify-content: ${slim ? 'flex-end' : 'center'};
            opacity: 0;
            will-change: opacity;
          `
        )}
        onClick={handleCreateGoal}
        flexDirection="column"
      >
        {closable ? (
          <FlexBox
            role="button"
            aria-label="close suggestion"
            className={css`
              width: 32px;
              height: 32px;
              position: absolute;
              top: 0;
              right: 0;
              z-index: 1;
            `}
            onClick={handleClose}
            centerAll
          >
            <Icon
              className={css`
                width: 16px;
              `}
              type="close"
              fill={theme.colors.section.textSecondary}
            />
          </FlexBox>
        ) : null}
        {hideLock || !slim ? null : (
          <img
            className={css`
              display: block;
              width: 29px;
              position: absolute;
              top: 8px;
              left: 8px;
            `}
            src={badge}
            alt="fable-lock-badge"
          />
        )}
        <div
          className={css`
            position: relative;
          `}
        >
          <img
            className={css`
              margin: ${slim ? '0 0 -12px' : '-12px 0 -40px'};
              display: block;
              width: 100%;
              max-width: ${slim ? 124 : 244}px;
              object-fit: contain;
            `}
            src={slim ? illoPortrait : illo}
            alt="reading goals stats"
          />
          {hideLock || slim ? null : (
            <img
              className={css`
                display: block;
                width: 29px;
                position: absolute;
                top: -4px;
                left: calc(67%);
                transform: translateX(-50%);
                z-index: 1;
              `}
              src={badge}
              alt="fable-lock-badge"
            />
          )}
        </div>
        <FlexBox
          className={css`
            width: 100%;
            gap: 1px;
          `}
          flexDirection="column"
          alignItems="center"
        >
          <Text
            type="body"
            className={css`
              font-size: 12px;
              line-height: 16px;
              letter-spacing: -0.2px;
            `}
            weight="500"
            color={theme.colors.primary}
            align="center"
          >
            Suggested goal
          </Text>
          <Text
            tag="h4"
            type="header"
            className={css`
              font-size: 24px;
              line-height: 24px;
              letter-spacing: -0.5px;
            `}
            weight="700"
            color={theme.colors.primary}
            align="center"
          >
            {getLabel()}
          </Text>
          <Text
            type="body"
            className={css`
              width: 100%;
              max-width: 270px;
              font-size: 12px;
              line-height: 16px;
              letter-spacing: -0.2px;
            `}
            weight="500"
            color={theme.colors.section.textSecondary}
            align="center"
          >
            this {period}
          </Text>
          {!slim ? (
            <CreateReadingGoalButton
              className={css`
                margin-top: 7px;
              `}
            />
          ) : null}
        </FlexBox>
      </FlexBox>
    </div>
  )
}
